import { type Registry as Services, service } from '@ember/service';
import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { Period } from 'lumen/models';
import type {
  MinLosOverride,
  OverbookingOverride,
} from 'lumen/models/override';
import type { HoteldateTransformReturn } from 'lumen/transforms/hoteldate';
import {
  formatRestrictionValueUtility,
  type RestrictionValue,
} from 'lumen/utils/channel-pricing/restrictions';
import {
  RESTRICTION_TYPES,
  type RestrictionModelContextsType,
} from 'lumen/utils/constants/channel-pricing-constants';
import { RESTRICTION_API_MESSAGE_VALUES } from 'lumen/utils/constants/channel-pricing-constants';
import { OVERRIDE_TYPES } from 'lumen/utils/constants/pricing-assistant-constants';
import {
  isDeepDiscountRateGroup,
  isHotelLevel,
} from 'lumen/utils/pricing-assistant/restriction-action-utils';

export default class Restriction extends Model {
  [Type] = 'restriction' as const;

  @belongsTo('period', { async: false, inverse: 'restrictions' })
  declare period: Period;
  @hasMany('override', { async: false, inverse: 'restriction' })
  declare overrides: (OverbookingOverride | MinLosOverride)[];

  @attr('string') declare channelId: string;
  @attr('string')
  declare context: RestrictionModelContextsType;
  @attr('hoteldate') declare fromDate: NonNullable<HoteldateTransformReturn>;
  @attr('string') declare message: string;

  @attr('string') declare rateGroupId: string | null;
  @attr('string') declare rateId: string;
  @attr('string') declare roomId: string;
  @attr('number') declare subscriptionId: number;

  @attr('json') declare values: RestrictionValue[];

  @service declare intl: Services['intl'];

  /* Maybe later there can be multiple restriction values, but haven't gotten more BE updates yet */
  get formattedValue(): string | undefined {
    // SMART DISTRIBUTION TODO: Currently taking first value here for now
    return this.formattedValues[0]?.formattedValue;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedValues() {
    return this.values.map((restrictionValueObject) => {
      return {
        ...restrictionValueObject,
        ...this.formatRestrictionValue(restrictionValueObject),
      };
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasNoValues() {
    return this.values.length === 0;
  }

  get hasOverride(): boolean {
    return this.overrides.length > 0;
  }

  /* In some cases the API can return a restriction object without a value, but just a message to indicate a certain state/inconsistency in the setup */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasRestrictionNotPresentOnLoadingRoomMessage() {
    return (
      this.message ===
      RESTRICTION_API_MESSAGE_VALUES.RESTRICTION_NOT_PRESENT_ON_LEADING_ROOM_BASE_RATE
    );
  }

  get isDeepDiscountRateGroup(): boolean {
    return isDeepDiscountRateGroup(this);
  }

  get isForMinorChannel(): boolean {
    return this.channelId === 'OTHER';
  }

  get isHotelLevel(): boolean {
    return isHotelLevel(this);
  }

  /* Applied MinLos value (Hotel vs. Deep discount rate group) */
  get minLosAppliedContext(): RestrictionModelContextsType | null {
    return this.minLosRestrictionValueObject?.appliedContext ?? null;
  }

  /* The MinLOS override that applies to this restriction. The BE maps each
     override to its correct restriction, so matching on type alone is enough. */
  get minLosOverride(): MinLosOverride | null {
    return (
      this.overrides.find(
        (override) => override.type === OVERRIDE_TYPES.MIN_LOS,
      ) ?? null
    );
  }

  get minLosRestrictionValueObject(): RestrictionValue | undefined {
    return this.getRestrictionValueObject(RESTRICTION_TYPES.MIN_LOS);
  }

  get minLosValue(): number | null {
    return this.minLosRestrictionValueObject?.value ?? null;
  }

  get overbookingOverride(): OverbookingOverride | null {
    return (
      this.overrides.find(
        (override) => override.type === RESTRICTION_TYPES.OVERBOOKING,
      ) ?? null
    );
  }

  get overbookingRestrictionValueObject(): RestrictionValue | undefined {
    return this.getRestrictionValueObject(RESTRICTION_TYPES.OVERBOOKING);
  }

  get overbookingValue(): number | null {
    return this.overbookingRestrictionValueObject?.value ?? null;
  }

  // SMART DISTRIBUTION TODO: can we move the formatRestrictionValue to a util later? Seems to be reusable in both restriction and restriction-recommendation model
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formatRestrictionValue(restrictionValueObject: RestrictionValue) {
    return formatRestrictionValueUtility(restrictionValueObject, this.intl);
  }

  private getRestrictionValueObject(
    type: RestrictionValue['type'],
  ): RestrictionValue | undefined {
    return this.values.find((value) => value.type === type);
  }
}
